/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase'
import type { User } from '@supabase/supabase-js'
import { identifyUser, trackEvent, resetAnalytics } from '@/lib/analytics'

export interface Profile {
  id: string
  role: 'contractor' | 'operator' | 'admin'
  business_name: string
  contact_name: string
  email: string
  phone: string | null
  logo_path: string | null
  address_line1: string | null
  address_line2: string | null
  city: string | null
  state_province: string | null
  postal_code: string | null
  country_code: string
  default_currency: string
  timezone: string
  locale: string | null
  localization_confirmed_at: string | null
  plan_code: 'FREE' | 'ONE_TIME' | 'CORE' | 'PREMIUM'
  one_time_credits: number
  one_time_credits_expire_at: string | null
  onboarding_completed_at: string | null
  stripe_customer_id: string | null
  created_at: string
  updated_at: string
}

interface AuthContextType {
  user: User | null
  profile: Profile | null
  loading: boolean
  onboardingLoading: boolean
  signIn: (email: string) => Promise<{ error: Error | null }>
  signInWithGoogle: () => Promise<{ error: Error | null }>
  signOut: () => Promise<{ error: Error | null }>
  refreshProfile: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const userRef = React.useRef<User | null>(user)
  useEffect(() => {
    userRef.current = user
  }, [user])
  const [profile, setProfile] = useState<Profile | null>(null)
  const [loading, setLoading] = useState(true)
  const [onboardingLoading, setOnboardingLoading] = useState(false)

  // Fetch public.profiles record
  async function fetchProfile(userId: string) {
    try {
      const { data, error } = (await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single()) as { data: Profile | null; error: Error | null }

      if (error) {
        console.error('Error fetching profile:', error)
        setProfile(null)
      } else {
        setProfile(data)
      }
    } catch (err) {
      console.error('Unexpected profile fetch error:', err)
      setProfile(null)
    }
  }

  // Refresh profile data manually (e.g. after onboarding or settings update)
  async function refreshProfile() {
    if (user) {
      setOnboardingLoading(true)
      await fetchProfile(user.id)
      setOnboardingLoading(false)
    }
  }

  useEffect(() => {
    let mounted = true

    // Initialize session
    async function initSession() {
      try {
        const { data: { session } } = await supabase.auth.getSession()
        if (mounted) {
          if (session) {
            setUser(session.user)
            await fetchProfile(session.user.id)
          } else {
            setUser(null)
            setProfile(null)
          }
        }
      } catch (err) {
        console.error('Error initializing session:', err)
      } finally {
        if (mounted) {
          setLoading(false)
        }
      }
    }

    void initSession()

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (!mounted) return

        if (session) {
          const isNewLogin = !userRef.current && session.user
          setUser(session.user)
          if (isNewLogin) {
            identifyUser(session.user.id)
            trackEvent('sign_in_completed')
          }
          await fetchProfile(session.user.id)
        } else {
          setUser(null)
          setProfile(null)
        }
        setLoading(false)
      }
    )

    return () => {
      mounted = false
      subscription.unsubscribe()
    }
  }, [])

  // Send magic link
  async function signIn(email: string) {
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: `${window.location.origin}/`,
        },
      })
      return { error: error as Error | null }
    } catch (err) {
      return { error: err as Error }
    }
  }

  // Sign in with Google OAuth
  async function signInWithGoogle() {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/`,
        },
      })
      return { error: error as Error | null }
    } catch (err) {
      return { error: err as Error }
    }
  }

  // Sign out
  async function signOut() {
    try {
      trackEvent('sign_out')
      resetAnalytics()
      const { error } = await supabase.auth.signOut()
      if (!error) {
        setUser(null)
        setProfile(null)
      }
      return { error: error as Error | null }
    } catch (err) {
      return { error: err as Error }
    }
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        profile,
        loading,
        onboardingLoading,
        signIn,
        signInWithGoogle,
        signOut,
        refreshProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
